import { useEffect, useRef, useState } from 'react';
import { FrameStatus, DomElement, Frame, ToStudio, SelectionInfo } from 'dragonmouse-shared/interfaces';
import { computeViewport, Size, Viewport } from './util.ts';
import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import Button, { ButtonType } from 'components/Widget/Button/Button';
import { unthemed } from 'tokens';
import forge from 'apis/forge';
import * as S from './styles.ts';
import { DEFAULT_CANVAS_COLOR, selectActiveScene } from 'redux/db/reducers.ts';
import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { frameImageUrl } from 'utils';
import { useImageLoading } from 'hooks/useImageLoading';
import { onToStudio, sendToDevigner } from '../../../devigner.ts';
import { Point } from 'dragonmouse-shared/geometry.ts';

interface PrototypeProps {
	frame: Frame;
	siteUrl: string | null;
	isDragging: boolean;
	selectMode: boolean;
	fullscreenMode: boolean;
	onSelectElement: (element: DomElement) => void;
	onSetSelectMode: (selectMode: boolean) => void;
	onSetSelectedElements: (elements: DomElement[]) => void;
	onIframeReadyChange: (ready: boolean) => void;
	onContextMenu?: (selectionInfo: SelectionInfo, point: Point) => void;
	prototypeFrameSize?: { width: number; height: number };
	onSetFrameSize?: (size: { width: number; height: number }) => void;
}

const Prototype = ({
	frame,
	siteUrl,
	isDragging,
	selectMode,
	fullscreenMode,
	onSelectElement,
	onSetSelectMode,
	onSetSelectedElements,
	onIframeReadyChange,
	onContextMenu,
	prototypeFrameSize,
	onSetFrameSize,
}: PrototypeProps) => {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [iframeReady, setIframeReady] = useState(false);
	const [currentFrameSize, setCurrentFrameSize] = useState<Size>({
		width: prototypeFrameSize?.width ?? frame.width,
		height: prototypeFrameSize?.height ?? frame.height,
	});
	const [isResizing, setIsResizing] = useState(false);
	const [preservedScale, setPreservedScale] = useState<number | null>(null);
	const [containerSize, setContainerSize] = useState<Size>({ width: 0, height: 0 });
	const [viewport, setViewport] = useState<Viewport | null>(null);
	const imageSrc = frameImageUrl(frame);
	const [isImageLoading, handleImageRef] = useImageLoading(imageSrc ?? '');

	const { scene, prototypeScaling } = useSelector((r: RootReducer) => {
		return {
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			prototypeScaling: r.mode.prototypeScaling,
		};
	});

	// Helper function to update frame size and notify parent
	const updateFrameSize = (newSize: { width: number; height: number }) => {
		setCurrentFrameSize(newSize);
		onSetFrameSize?.(newSize);
	};

	// Watch container size changes
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const { width, height } = entry.contentRect;
				setContainerSize({ width, height });
			}
		});

		resizeObserver.observe(container);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	// Re-initialize currentFrameSize when frame changes
	useEffect(() => {
		setCurrentFrameSize({ width: frame.width, height: frame.height });
	}, [frame.width, frame.height]);

	// Sync with prototypeFrameSize prop when it changes
	useEffect(() => {
		if (prototypeFrameSize) {
			setCurrentFrameSize({ width: prototypeFrameSize.width, height: prototypeFrameSize.height });
		}
	}, [prototypeFrameSize?.width, prototypeFrameSize?.height]);

	// Recalculate viewport when dependencies change
	useEffect(() => {
		if (containerSize.width === 0 || containerSize.height === 0) return; // Wait for container to be measured

		const newViewport = computeViewport(
			currentFrameSize,
			containerSize,
			prototypeScaling,
			fullscreenMode
		);
		setViewport(newViewport);
	}, [currentFrameSize, containerSize, prototypeScaling, fullscreenMode, preservedScale]);

	// Handle all iframe messages internally (only when siteUrl exists)
	useEffect(() => {
		if (!siteUrl || !frame?.siteId) return;

		const listener = onToStudio(iframeRef.current, frame.siteId, (toStudio: ToStudio) => {
			const iframeRect = iframeRef.current?.getBoundingClientRect();

			if (toStudio.type === 'contextmenu') {
				if (!viewport) return;
				if (!iframeRect) return;
				const point = {
					x: toStudio.point.x,
					y: toStudio.point.y,
				};
				const adjustedPoint = {
					x: iframeRect.left + point.x * viewport.scale,
					y: iframeRect.top + point.y * viewport.scale + 25,
				};
				let selectionInfo: SelectionInfo | null = null;
				if (toStudio.selectionInfo) {
					try {
						selectionInfo = JSON.parse(toStudio.selectionInfo) as SelectionInfo;
						onContextMenu?.(selectionInfo, adjustedPoint);
					} catch (e) {
						console.error('Invalid selectionInfo JSON', e);
					}
				}
			} else if (toStudio.type === 'select') {
				const selectedElement: DomElement = {
					id: toStudio.id,
					tagName: toStudio.tagName,
					className: toStudio.className,
				};
				onSelectElement(selectedElement);
			} else if (toStudio.type === 'ready') {
				if (frame.siteId) {
					sendToDevigner(iframeRef.current, frame.siteId, {
						type: selectMode ? 'startOverlay' : 'stopOverlay',
					});
				}
			} else if (toStudio.type === 'keypress') {
				// handle key presses from within the iframe
				if (toStudio.key === 'Escape') {
					onSetSelectMode(false);
					onSetSelectedElements([]);
				}
			} else if (toStudio.type === 'devignerReady') {
				setIframeReady(true);
			}
		});

		return listener;
	}, [
		siteUrl,
		frame?.siteId,
		onSelectElement,
		onSetSelectMode,
		onSetSelectedElements,
		viewport?.scale,
		viewport?.x,
		viewport?.y,
		selectMode,
		onContextMenu,
	]);

	useEffect(() => {
		if (!siteUrl || !frame?.siteId) return;
		sendToDevigner(iframeRef.current, frame.siteId, {
			type: selectMode ? 'startOverlay' : 'stopOverlay',
		});
	}, [selectMode, siteUrl, frame.siteId]);

	// changing siteUrl should cause the <iframe> to be re-initialized
	useEffect(() => {
		setIframeReady(false);
	}, [siteUrl]);

	// Notify parent when iframe ready state changes
	// For images (when siteUrl is null), consider them ready when not loading
	useEffect(() => {
		if (siteUrl) {
			onIframeReadyChange(iframeReady);
		} else {
			// For images, we're ready when the image is not loading
			onIframeReadyChange(!isImageLoading);
		}
	}, [iframeReady, isImageLoading, siteUrl, onIframeReadyChange]);

	// Store current scale when view method changes
	useEffect(() => {
		if (frame && viewport) {
			// Store the current scale for preservation
			setPreservedScale(viewport?.scale);
		}
	}, [prototypeScaling, frame, viewport?.scale]);

	// Handle resize logic
	const handleResizeStart = (e: React.MouseEvent, side: 'left' | 'right') => {
		e.preventDefault();
		setIsResizing(true);

		const startX = e.clientX;
		const startWidth = currentFrameSize.width;

		const handleMouseMove = (e: MouseEvent) => {
			const deltaX = e.clientX - startX;
			const scale = viewport?.scale;
			if (!scale) return;
			const deltaWidth = deltaX / scale;

			let newWidth = startWidth;
			if (side === 'right') {
				newWidth = Math.max(200, startWidth + deltaWidth);
			} else {
				newWidth = Math.max(200, startWidth - deltaWidth);
			}

			updateFrameSize({ width: newWidth, height: currentFrameSize.height });
		};

		const handleMouseUp = () => {
			setIsResizing(false);
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	const resetFrameAfterError = async () => {
		await forge.updateFrame(
			{
				update: {
					id: frame.id,
					metadata: {
						...frame.metadata,
						status: FrameStatus.READY,
						statusMessage: '',
					},
				},
			},
			true
		);
	};

	const renderStatusOverlay = () => {
		if (!frame.metadata.status || frame.metadata.status === FrameStatus.READY || !viewport)
			return null;

		return (
			<S.FrameStatusOverlay
				x={viewport.x}
				y={viewport.y}
				width={viewport.width}
				height={viewport.height}
				scale={viewport.scale}
			>
				<S.FrameStatusContent>
					<S.ScaledStatusIndicator scale={viewport.scale}>
						{frame.metadata.status === FrameStatus.ERROR && (
							<Icon type={IconType.ERROR} width={48} height={48} color={unthemed.gray[100]} />
						)}
						{frame.metadata.status === FrameStatus.PROCESSING && <Loader width={48} height={48} />}
					</S.ScaledStatusIndicator>
					<S.ScaledOverlayText scale={viewport.scale}>
						{frame.metadata.statusMessage || ''}
					</S.ScaledOverlayText>
					{frame.metadata.status === FrameStatus.ERROR && (
						<S.ScaledButton scale={viewport.scale}>
							<Button onClick={resetFrameAfterError} label="Revert" type={ButtonType.SECONDARY} />
						</S.ScaledButton>
					)}
				</S.FrameStatusContent>
			</S.FrameStatusOverlay>
		);
	};

	const canvasColor = scene?.metadata?.canvasColor ?? DEFAULT_CANVAS_COLOR;

	// Get safe background color - handle CSS variables that don't work in this context
	const safeBackgroundColor =
		frame.metadata.backgroundColor?.startsWith('var(')
			? undefined // this will trigger the default when passed to FrameContainer
			: frame.metadata.backgroundColor;

	return (
		<S.PrototypeContainer ref={containerRef} backgroundColor={canvasColor}>
			{viewport && (siteUrl || imageSrc) && (
				<S.FrameContainer
					x={viewport.x}
					y={viewport.y}
					width={viewport.width}
					height={viewport.height}
					scale={viewport.scale}
					backgroundColor={safeBackgroundColor}
				>
					{siteUrl ? (
						<iframe
							ref={iframeRef}
							src={siteUrl}
							title="Frame Prototype"
							style={{
								width: '100%',
								height: '100%',
								border: 'none',
								pointerEvents: isDragging || isResizing ? 'none' : 'auto',
							}}
						/>
					) : imageSrc ? (
						<img
							key={imageSrc}
							ref={handleImageRef}
							src={imageSrc}
							alt={frame.title || 'Frame'}
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'contain',
								pointerEvents: isDragging || isResizing ? 'none' : 'auto',
							}}
						/>
					) : null}
				</S.FrameContainer>
			)}
			{renderStatusOverlay()}
			{!fullscreenMode && viewport && (siteUrl || imageSrc) && (
				<>
					<S.ResizeHandle
						side="left"
						x={viewport.x - 2}
						y={viewport.y}
						height={viewport.height * viewport.scale}
						onMouseDown={(e) => handleResizeStart(e, 'left')}
					/>
					<S.ResizeHandle
						side="right"
						x={viewport.x + viewport.width * viewport.scale - 2}
						y={viewport.y}
						height={viewport.height * viewport.scale}
						onMouseDown={(e) => handleResizeStart(e, 'right')}
					/>
				</>
			)}
		</S.PrototypeContainer>
	);
};

export default Prototype;
